import FilterAlt from "@mui/icons-material/FilterAlt";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import type { AlertColor } from "@mui/material";
import {
	Autocomplete, Box, Checkbox,
	IconButton, Tooltip, Typography
} from "@mui/material";
import {
	type GridColumnVisibilityModel,
	getGridDateOperators,
	type GridRowId,
	type GridRowSelectionModel,
	useGridApiRef} from "@mui/x-data-grid-pro";
import axios from "axios";
import {
	useCallback, useEffect, useMemo,
	useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import apiRoutes from "../../constants/api-routes";
import messageLevels from "../../constants/message-levels";
import messages from "../../constants/messages";
import pages from "../../constants/pages";
import { arrayOperators, handleArrayFiltering, handleDateFiltering, handleStatusQuickFilter, statusOperators } from "../../helpers/datagridFiltering";
import { emptyGridRowSelectionModel } from "../../helpers/dataGridRowSelectionModel";
import useAuthHeader from "../../hooks/useAuthHeader";
import useCoAppNavigation from "../../hooks/useCoAppNavigation";
import useDatagridSorting from "../../hooks/useDatagridSorting";
import useDynamicContainerHeight from "../../hooks/useDynamicContainerHeight";
import useToastAlert from "../../hooks/useToastAlert";
import { selectColumnVisibilityForGrid, setGridColumnVisibility } from "../../redux/dataGridPrefsSlice";
import CoAppColumnsButton from "../global/components/datagrid/CoAppColumnsButton";
import CoAppDataGridHeader from "../global/components/datagrid/CoAppDatagridHeader";
import type { InlineTag } from "../global/components/datagrid/CoAppInlineTagsCell";
import DataGridCustomNoRowsOverlay from "../global/components/datagrid/DataGridCustomNoRowsOverlay";
import useFilteredDataRowCount from "../global/components/datagrid/useFilteredDataRowCount";
import useGridQuickFilterModel from "../global/components/datagrid/useGridQuickFilterModel";
import CoAppTextField from "../global/components/inputs/CoAppTextField";
import CoAppBulkApplyModal from "../global/components/modals/CoAppBulkApplyModal";
import CoAppConfirmationModal from "../global/components/modals/CoAppConfirmationModal";
import CoAppDestructiveConfirmationModal from "../global/components/modals/CoAppDestructiveConfirmationModal";
import UndoSnackbar from "../global/components/UndoSnackbar";
import { CoAppDataGrid, CoAppDataGridContainer, CoAppDataGridToolbar } from "../global/styled/global.styled";
import { arrayColumns, getRuleLibraryColumns, type RuleLibraryGridRow } from "./columns/rule-library-columns";
import RuleDataGridSelectionRow from "./RuleDataGridSelectionRow";

const datagridInitialState = {
	filter: {
		filterModel: {
			items: [],
			quickFilterExcludeHiddenColumns: true,
		},
	},
};

const dateColumns = ["createdAt", "updatedAt", "lastCompiled"];

/** Tag/group row from GET tags/groups or bulk-apply UI. */
type TagOrGroup = { id?: number | string; name: string };
type BulkNamed = { name: string };

export default function RuleLibraryDataGrid() {
	const authHeader = useAuthHeader();
	// Extra 60px reserves space for the structural footer below the container.
	const { containerRef, containerHeight } = useDynamicContainerHeight(72);
	const { openRuleWizard } = useCoAppNavigation();
	const { handleToastAlert } = useToastAlert();
	const { sortModel, handleSortModelChange, handleInitializeSortModel } = useDatagridSorting("ruleLibrary");
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const ruleColumnVisibility = useSelector(selectColumnVisibilityForGrid("rules"));
	const handleRuleColumnVisibilityChange = (next: GridColumnVisibilityModel) =>
		dispatch(setGridColumnVisibility({ gridKey: "rules", model: next }));
	const [rules, setRules] = useState<RuleLibraryGridRow[]>([]);
	const [loading, setLoading] = useState(true);
	const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(() => emptyGridRowSelectionModel());
	const { gridFilterModel, onFilterModelChange, quickFilterSearchProps } = useGridQuickFilterModel();
	const apiRef = useGridApiRef();
	const { visibleRuleCount } = useFilteredDataRowCount(apiRef, rules, loading, gridFilterModel);
	// Element the filter panel anchors to, so it opens directly under the funnel
	// filter button instead of floating in the middle of the grid.
	const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);
	// A column filter (not the quick-search text) is considered "applied" when
	// the filter model has at least one item — used to swap the filter icon.
	const hasActiveFilter = (gridFilterModel.items?.length ?? 0) > 0;

	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarLevel, setSnackbarLevel] = useState<AlertColor | "">("");
	const [isBulkUndo, setIsBulkUndo] = useState(false);
	const [rulesToUndo, setRulesToUndo] = useState<GridRowId[]>([]);
	const [destructiveModalOpen, setDestructiveModalOpen] = useState(false);
	const [ruleBeingDeleted, setRuleBeingDeleted] = useState<RuleLibraryGridRow>({ name: "" });
	const [bulkApplyModalOpen, setBulkApplyModalOpen] = useState(false);
	const [bulkDestructiveModalOpen, setBulkDestructiveModalOpen] = useState(false);
	const [bulkStatusUpdateOpen, setBulkStatusUpdateModalOpen] = useState(false);
	const [isBulkActivating, setIsBulkActivating] = useState(false);
	const [bulkStatusUpdateTitle, setBulkStatusUpdateTitle] = useState("");
	const [bulkApplyAvailableItems, setBulkApplyAvailableItems] = useState<TagOrGroup[]>([]);
	const [bulkApplyItems, setBulkApplyItems] = useState<BulkNamed[]>([]);
	const [bulkApplyItemType, setBulkApplyItemType] = useState("");
	const [bulkApplyRemovedItems, setBulkApplyRemovedItems] = useState<BulkNamed[]>([]);
	const [activateIsDisabled, setActivateIsDisabled] = useState(false);
	const [deactivateIsDisabled, setDeactivateIsDisabled] = useState(false);

	// QA fix: MUI X v8 represents "select all" as an EXCLUDE-type model with an
	// empty `ids` set, so reading `selectionModel.ids` directly counts 0 rows
	// when everything is selected — leaving the bulk-action bar disabled and the
	// operations no-ops. Resolve the concrete selected ids for both include and
	// exclude models so select-all enables and drives the bulk actions.
	const selectedRuleIds = useMemo<GridRowId[]>(() => {
		const dataRows = rules.filter((r) => r.id != null);
		if (selectionModel.type === "exclude") {
			return dataRows.filter((r) => !selectionModel.ids.has(r.id as GridRowId)).map((r) => r.id as GridRowId);
		}
		return dataRows.filter((r) => selectionModel.ids.has(r.id as GridRowId)).map((r) => r.id as GridRowId);
	}, [rules, selectionModel]);

	const model = bulkApplyItemType.toLowerCase().includes("tag") ? "tags" : "groups";

	const initRuleLibrary = useCallback(() => {
		setLoading(true);
		setRules([]);
		setSelectionModel(emptyGridRowSelectionModel());
		axios.get(apiRoutes.getRules + "?isTrashed=0", { headers: authHeader })
			.then(res => {
				const loadedRules = res.data.rules.map(rule => {
					return {
						...rule,
						props: rule.props || {}
					};
				});
				setRules(loadedRules as RuleLibraryGridRow[]);
			})
			.catch(err => console.log(err))
			.finally(() => setLoading(false));
	}, [authHeader]);

	const handleStatusToggleChange = useCallback((rule: RuleLibraryGridRow) => {
		const ruleId = rule.id;
		if (ruleId === undefined || ruleId === null) return;
		axios.put(apiRoutes.toggleRuleStatus(ruleId), {}, { headers: authHeader })
			.then(() => {
				initRuleLibrary();
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, rule.active ? messages.RULE_DEACTIVATION_ERROR_MSG : messages.RULE_ACTIVATION_ERROR_MSG);
			});
	}, [authHeader, initRuleLibrary, handleToastAlert]);

	const handleAddRuleClick = () => {
		openRuleWizard();
	};

	const handleEditRuleClick = useCallback((ruleId: number | string) => {
		navigate(pages.editRule(ruleId));
	}, [navigate]);

	const handleSoftDeleteRule = () => {
		const ruleId = ruleBeingDeleted.id;
		if (ruleId === undefined || ruleId === null) return;
		axios.delete(apiRoutes.deleteRule + "/" + ruleId, {
			headers: authHeader
		})
			.then(() => {
				setSnackbarLevel("info");
				setSnackbarMessage(messages.RULE_TRASHED_SUCCESS_MSG);
				setSnackbarOpen(true);
				setIsBulkUndo(false);
				initRuleLibrary();
				setDestructiveModalOpen(false);
			})
			.catch(err => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, messages.RULE_TRASHED_ERROR_MSG);
			});
	};

	const handleRestoreRule = () => {
		const ruleId = ruleBeingDeleted.id;
		if (ruleId === undefined || ruleId === null) return;
		axios.put(apiRoutes.restoreRule(ruleId), {}, {
			headers: authHeader
		})
			.then(() => {
				initRuleLibrary();
			})
			.catch(err => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, messages.RULE_RESTORE_ERROR_MSG);
			});
	};

	const handleBulkRuleRestoration = () => {
		const restorationData = {
			ruleIds: rulesToUndo
		};

		axios.put(apiRoutes.bulkRestoreRules, restorationData, { headers: authHeader })
			.then(() => {
				initRuleLibrary();
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.BULK_RESTORE_FAILED_TOAST_MSG);
			});
	};

	const handleCopyRuleClick = useCallback((ruleId: number | string) => {
		axios.post(apiRoutes.copyRule(ruleId), {}, {
			headers: authHeader
		})
			.then(() => {
				initRuleLibrary();
			})
			.catch(err => {
				console.error(err);
				handleToastAlert(messageLevels.ERROR, messages.RULE_COPY_ERROR_MSG);
			});
	}, [authHeader, initRuleLibrary, handleToastAlert]);

	const handleDeleteRuleToggle = useCallback((rule: RuleLibraryGridRow = { name: "" }) => {
		setDestructiveModalOpen(prev => !prev);
		setRuleBeingDeleted(rule);
	}, []);

	// Inline tag editor — optimistic update + fetch-merge-put. The backend's
	// `updateRule` controller expects the full rule body (nodes, transitions,
	// variables, name, platformId, etc.), so a partial `{tags: [...]}` PUT
	// is silently rejected. We GET the rule first, merge in the new tag
	// list, then PUT the full payload back. Two requests but reliable.
	const persistRuleTags = useCallback((rule: RuleLibraryGridRow, nextTagList: InlineTag[]) => {
		const ruleId = rule.id ?? rule.uuid;
		if (!ruleId) return;
		// Optimistic local update so the chip appears immediately.
		setRules((prev) => prev.map((r) => (
			(r.id ?? r.uuid) === ruleId ? { ...r, tags: nextTagList } : r
		)));

		const handleError = (err) => {
			const status = err?.response?.status;
			const body = err?.response?.data;
			console.error("Failed to update rule tags", { status, body, err });
			handleToastAlert(
				messageLevels.ERROR,
				`Failed to update tags${status ? ` (${status})` : ""}. Reverting.`
			);
			initRuleLibrary();
		};

		axios.get(apiRoutes.getRule(ruleId), { headers: authHeader })
			.then((getRes) => {
				const fullRule = getRes.data?.rule ?? getRes.data;
				if (!fullRule) throw new Error("Rule not found in GET response");
				const persistableTags = nextTagList.map(tag => tag.name);
				const merged = { ...fullRule, tags: persistableTags };
				return axios.put(
					apiRoutes.updateRule(ruleId),
					merged,
					{ headers: authHeader }
				);
			})
			.catch(handleError);
	}, [authHeader, initRuleLibrary, handleToastAlert]);

	const handleAddTagToRule = useCallback((rule: RuleLibraryGridRow, tag: InlineTag) => {
		const current = Array.isArray(rule.tags) ? rule.tags : [];
		if (current.some((t) => String(t.id) === String(tag.id))) return;
		persistRuleTags(rule, [...current, tag]);
	}, [persistRuleTags]);

	const handleRemoveTagFromRule = useCallback((rule: RuleLibraryGridRow, tag: InlineTag) => {
		const current = Array.isArray(rule.tags) ? rule.tags : [];
		persistRuleTags(rule, current.filter((t) => String(t.id) !== String(tag.id)));
	}, [persistRuleTags]);

	// Column defs must keep a stable identity: a new `columns` array makes MUI X
	// rebuild its column state and discard the widths from `autosizeOnMount`,
	// so the grid visibly re-flowed on every selection/state change.
	const columns = useMemo(
		() => getRuleLibraryColumns(
			handleStatusToggleChange,
			handleEditRuleClick,
			handleCopyRuleClick,
			handleDeleteRuleToggle,
			handleAddTagToRule,
			handleRemoveTagFromRule
		),
		[handleStatusToggleChange, handleEditRuleClick, handleCopyRuleClick, handleDeleteRuleToggle, handleAddTagToRule, handleRemoveTagFromRule]
	);

	function ArrayTypeInput(props) {
		const { item, applyValue, itemType } = props;
		const [items, setItems] = useState<TagOrGroup[]>([]);

		useEffect(() => {
			const url = itemType === "tags" ? apiRoutes.getTags : apiRoutes.getGroups;
			axios.get(url, { headers: authHeader })
				.then(res => setItems(res.data))
				.catch(err => {
					console.error(err);
					handleToastAlert(messageLevels.ERROR, `Failed to load ${itemType ?? "filter"} options.`);
				});
		}, [itemType, authHeader]);

		const handleSelectionChange = (event, value, reason) => {
			applyValue({ ...item, value: value });
		};

		const handleCheckboxRenderOptions = (props, option, { selected }) => {
			const { key, ...optionProps } = props;
			return (
				<li key={key ?? option.id ?? option.name} {...optionProps}>
					<Checkbox
						style={{ marginRight: 5 }}
						checked={selected}
						value={option.name}
					/>
					<Typography variant="body2" color="text.primary">{option.name}</Typography>
				</li>
			);
		};

		return (
			<Autocomplete
				multiple
				size="small"
				sx={{ marginTop: "-16px" }}
				limitTags={2}
				options={items}
				getOptionLabel={(option) => option.name}
				value={item.value}
				onChange={handleSelectionChange}
				disableCloseOnSelect
				renderInput={(params) => (
					<CoAppTextField
						{...params}
						label="Filter value"
						placeholder={`Search ${itemType === "tags" ? "tags" : "groups"}`}
						error={props.error}
						helperText={props.helperText}
						FormHelperTextProps={{ style: { color: "#DE3730" } }}
					/>
				)
				}
				renderOption={handleCheckboxRenderOptions}
			/>
		);
	}

	const invisibleFields = ["id"];
	const filterableColumns = useMemo(
		() => columns
			.filter((column) => !invisibleFields.includes(column.field))
			.map((column) => {
				if (arrayColumns.includes(column.field)) {
					return {
						...column,
						getApplyQuickFilterFn: handleArrayFiltering,
						filterOperators: [
							...arrayOperators,
							{
								label: "has any of",
								value: "hasAnyOf",
								getApplyFilterFn: (filterItem) => {
									return (value) => {
										if (!filterItem.value) return null;
										const valueNames = value.map(item => item.name.toLowerCase());
										const filterItemNames = filterItem.value.map(item => item.name.toLowerCase());
										return valueNames.some(name => filterItemNames.includes(name));
									};
								},
								InputComponent: ArrayTypeInput,
								InputComponentProps: {
									itemType: column.field
								}
							}
						]
					};
				} else if (dateColumns.includes(column.field)) {
					return {
						...column,
						getApplyQuickFilterFn: handleDateFiltering,
						filterOperators: getGridDateOperators(true)
					};
				} else if (column.field === "active") {
					return {
						...column,
						getApplyQuickFilterFn: handleStatusQuickFilter,
						filterOperators: statusOperators
					};
				} else {
					return column;
				}
			})
			.map((c) => ((["name", "description"].includes(c.field) && c.flex == null)
				? { ...c, flex: 1, minWidth: c.minWidth ?? c.width ?? 200 }
				: c)),
		[columns]);

	const CustomToolbar = useCallback(() => {
		return (
			<div style={{ width: "100%" }}>
				<CoAppDataGridToolbar container>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
						{/* Filter trigger moved next to the search field in the
						    header (see `filterControl` below). The column picker
						    stays here on the toolbar's trailing edge. */}
						<CoAppColumnsButton
							gridKey="rules"
							columns={filterableColumns}
							resourceCount={loading ? undefined : { count: rules.length, label: "rules" }}
						/>
					</Box>
					{/* Pagination removed — DataGridPro virtualizes rows so
					    users scroll the body instead of paging. */}
				</CoAppDataGridToolbar>
			</div>
		);
	}, [loading, filterableColumns, rules.length]);

	const handleBulkApplyModalToggle = (model: "tags" | "groups") => {
		setBulkApplyModalOpen(true);
		setBulkApplyItemType(model === "tags" ? "Tags" : "Groups");
		const apiRoute = model === "tags" ? apiRoutes.getTags : apiRoutes.getGroups;
		axios.get(apiRoute, { headers: authHeader })
			.then(res => {
				setBulkApplyAvailableItems(res.data as TagOrGroup[]);
				const bulkItems: BulkNamed[] = [];
				for (const ruleId of selectedRuleIds) {
					const foundRule = rules.find(rule => rule.id === ruleId);
					if (!foundRule) continue;
					const collection = model === "tags" ? foundRule.tags : foundRule.groups;
					if (Array.isArray(collection) && collection.length > 0) {
						bulkItems.push(...collection.map((item: { name: string }) => ({ name: item.name })));
					}
				}
				const justNames = bulkItems.map(item => item.name);
				const uniqueItems = [...new Set(justNames)].map(name => ({ name }));
				setBulkApplyItems(uniqueItems);
			})
			.catch(err => console.log(err));
	};

	const handleBulkApplyModalClose = () => {
		setBulkApplyModalOpen(false);
		setBulkApplyItems([]);
		setBulkApplyAvailableItems([]);
		setBulkApplyItemType("");
		setBulkApplyRemovedItems([]);
	};

	const handleAddOrRemoveItemChange = (items: string[]) => {
		const removedItems = bulkApplyItems.filter((item) => !items.includes(item?.name ?? ""));
		if (removedItems.length > 0) {
			setBulkApplyRemovedItems(prev => [...prev, ...removedItems]);
		}
		setBulkApplyItems(items.map(itemName => { return { name: itemName }; }));
	};

	const handleBulkApplyConfirmation = () => {
		const putData = {
			ruleIds: selectedRuleIds,
			itemsToRemove: calculateItemsRemovedFromAll(),
			itemsToAdd: calculateItemsToAddToAll(),
			model: model
		};
		axios.post(apiRoutes.bulkApplyRuleChanges, putData, { headers: authHeader })
			.then(() => {
				initRuleLibrary();
				handleBulkApplyModalClose();
				setSelectionModel(emptyGridRowSelectionModel());
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.BULK_APPLY_FAILED_TOAST_MSG);
			});
	};

	const calculateItemsRemovedFromAll = () => {
		const itemsRemovedFromAll: TagOrGroup[] = [];
		for (const item of bulkApplyAvailableItems) {
			const isCurrentlyAppliedToRule = selectedRuleIds.some(ruleId => {
				const foundRule = rules.find(rule => rule.id === ruleId);
				if (!foundRule) return false;
				const coll = model === "tags" ? foundRule.tags : foundRule.groups;
				if (!Array.isArray(coll)) return false;
				return coll.map((i: { name: string }) => i.name).includes(item.name);
			});
			const isNotBeingAdded = !bulkApplyItems.map(bi => bi.name).includes(item.name);
			if ((isCurrentlyAppliedToRule && isNotBeingAdded)) {
				itemsRemovedFromAll.push(item);
			}
		}
		return itemsRemovedFromAll;
	};

	const calculateItemsToAddToAll = () => {
		const itemsToAdd: TagOrGroup[] = [];
		for (const item of bulkApplyAvailableItems) {
			const isCurrentlyAppliedToEveryRule = selectedRuleIds.every(ruleId => {
				const foundRule = rules.find(rule => rule.id === ruleId);
				if (!foundRule) return false;
				const coll = model === "tags" ? foundRule.tags : foundRule.groups;
				if (!Array.isArray(coll)) return false;
				return coll.map((i: { name: string }) => i.name).includes(item.name);
			});

			const unchangedItems = calculateUnchangedItems();
			const isUnchanged = unchangedItems.map(u => u.name).includes(item.name);
			const isSelected = bulkApplyItems.map(bi => bi.name).includes(item.name);
			if (!isCurrentlyAppliedToEveryRule && !isUnchanged && isSelected) {
				itemsToAdd.push(item);
			}
		}
		return itemsToAdd;
	};

	const calculateUnchangedItems = () => {
		const unchangedItems: BulkNamed[] = [];
		for (const item of bulkApplyItems) {
			const isCurrentlyAppliedToRule = selectedRuleIds.some(ruleId => {
				const foundRule = rules.find(rule => rule.id === ruleId);
				if (!foundRule) return false;
				const coll = model === "tags" ? foundRule.tags : foundRule.groups;
				if (!Array.isArray(coll)) return false;
				return coll.map((i: { name: string }) => i.name).includes(item.name);
			});

			const hasBeenRemovedBefore = bulkApplyRemovedItems.filter(removedItem => removedItem.name === item.name).length > 0;
			if (isCurrentlyAppliedToRule && !hasBeenRemovedBefore) {
				unchangedItems.push(item);
			}
		}
		return unchangedItems;
	};

	const handleRowSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
		const dataRows = rules.filter((r) => r.id != null);
		const selectedIds = newSelectionModel.type === "exclude"
			? dataRows.filter((r) => !newSelectionModel.ids.has(r.id as GridRowId)).map((r) => r.id as GridRowId)
			: dataRows.filter((r) => newSelectionModel.ids.has(r.id as GridRowId)).map((r) => r.id as GridRowId);
		const selectedSet = new Set(selectedIds);
		const fullRules = dataRows.filter((rule) => selectedSet.has(rule.id as GridRowId));
		const hasActiveRules = fullRules.some(rule => rule.active);
		const hasInactiveRules = fullRules.some(rule => !rule.active);

		if (!hasActiveRules) {
			setDeactivateIsDisabled(true);
		} else {
			setDeactivateIsDisabled(false);
		}
		if (!hasInactiveRules) {
			setActivateIsDisabled(true);
		} else {
			setActivateIsDisabled(false);
		}
		setSelectionModel(newSelectionModel);
	};

	const handleBulkSoftDeleteRuleToggle = () => {
		setBulkDestructiveModalOpen(prev => !prev);
	};

	const handleBulkSoftDeleteRulesConfirmation = () => {
		const deleteData = {
			ruleIds: selectedRuleIds
		};
		axios.delete(apiRoutes.bulkDeleteRules + "?isHardDelete=0", { headers: authHeader, data: deleteData })
			.then(() => {
				setSnackbarMessage(messages.SEND_RULE_TO_TRASH_TOAST_MSG(selectedRuleIds.length > 1));
				setSnackbarLevel("info");
				setSnackbarOpen(true);
				setIsBulkUndo(true);
				setRulesToUndo(selectedRuleIds);
				initRuleLibrary();
				handleBulkSoftDeleteRuleToggle();
				setSelectionModel(emptyGridRowSelectionModel());
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.BULK_SOFT_DELETE_FAILED_TOAST_MSG);
			});
	};

	const handleBulkStatusUpdateConfirmation = () => {
		const newStatus = isBulkActivating ? true : false;

		const putData = {
			ruleIds: getRulesToBeUpdated(),
			newStatus: newStatus
		};

		axios.put(apiRoutes.bulkUpdateRuleStatus, putData, { headers: authHeader })
			.then(() => {
				initRuleLibrary();
				setBulkStatusUpdateModalOpen(false);
				setSelectionModel(emptyGridRowSelectionModel());
			})
			.catch(err => {
				console.log(err);
				handleToastAlert(messageLevels.ERROR, messages.BULK_STATUS_UPDATE_FAILED_TOAST_MSG);
			});
	};

	const getRulesToBeUpdated = (): GridRowId[] => {
		const ruleIds: GridRowId[] = [];
		for (const ruleId of selectedRuleIds) {
			const rule = rules.find(totalRule => totalRule.id === ruleId);
			if (!rule || rule.id === undefined || rule.id === null) continue;
			if (isBulkActivating) {
				if (!rule.active) {
					ruleIds.push(rule.id);
				}
			} else {
				if (rule.active) {
					ruleIds.push(rule.id);
				}
			}
		}
		return ruleIds;
	};

	const getBulkStatusUpdateModalTitle = (isActivating: boolean) => {
		const objectifiedSelectedRules = selectedRuleIds
			.map(ruleId => rules.find(totalRule => totalRule.id === ruleId))
			.filter((r): r is RuleLibraryGridRow => r != null);

		if (isActivating) {
			const toBeActivatedCount = objectifiedSelectedRules.filter(rule => !rule.active).length;
			return `Activate selected inactive rules (${toBeActivatedCount})?`;
		} else {
			const toBeDeactivatedCount = objectifiedSelectedRules.filter(rule => rule.active).length;
			return `Deactivate selected active rules (${toBeDeactivatedCount})?`;
		}
	};

	const handleBulkStatusUpdateModalToggle = (status: "activate" | "deactivate") => {
		if (status === "activate") {
			setIsBulkActivating(true);
			setBulkStatusUpdateTitle(getBulkStatusUpdateModalTitle(true));
			setBulkStatusUpdateModalOpen(prev => !prev);
		} else {
			setIsBulkActivating(false);
			setBulkStatusUpdateTitle(getBulkStatusUpdateModalTitle(false));
			setBulkStatusUpdateModalOpen(prev => !prev);
		}
	};

	useEffect(() => {
		initRuleLibrary();
		handleInitializeSortModel();
	}, []);

	return (
		<>
			<CoAppDataGridHeader
				iconKind="rules"
				title="Rules"
				resourceType="Rule"
				addIsPresent={true}
				addOnClickHandler={handleAddRuleClick}
				editIsPresent={false}
				deleteIsPresent={false}
				tagManagementIsPresent={true}
				initRuleLibrary={initRuleLibrary}
				searchIsPresent={true}
				{...quickFilterSearchProps}
				searchPlaceholder="Search all columns…"
				filterControl={
					<Tooltip title={hasActiveFilter ? "Filters applied — edit" : "Filter"}>
						<IconButton
							ref={setFilterButtonEl}
							aria-label={hasActiveFilter ? "Edit filters (filters applied)" : "Filter"}
							onClick={() => apiRef.current?.showFilterPanel()}
							size="small"
							sx={{
								borderRadius: "8px",
								border: hasActiveFilter ? "1px solid rgba(2, 213, 171, 0.40)" : "1px solid rgba(13, 31, 48, 0.12)",
								background: hasActiveFilter ? "rgba(2, 213, 171, 0.12)" : "#FFFFFF",
								color: hasActiveFilter ? "#02B894" : "rgba(13, 31, 48, 0.55)",
								transition: "background 0.15s, border-color 0.15s, color 0.15s",
								"&:hover": { background: "rgba(2, 213, 171, 0.10)", borderColor: "rgba(2, 213, 171, 0.35)", color: "#02B894" },
							}}
						>
							{hasActiveFilter ? <FilterAlt sx={{ fontSize: 18 }} /> : <FilterAltOutlined sx={{ fontSize: 18 }} />}
						</IconButton>
					</Tooltip>
				}
			/>
			<CoAppDataGridContainer ref={containerRef} sx={{ height: containerHeight }}>
				<CoAppDataGrid
					apiRef={apiRef}
					sx={{}}
					loading={loading}
					rows={rules}
					columns={filterableColumns}
					checkboxSelection
					checkboxSelectionVisibleOnly
					disableRowSelectionOnClick
					// The app theme sets `disableColumnPinning: true` as a global
					// DataGrid default, which silently no-op'd the `pinnedColumns`
					// below — so the actions column wasn't actually sticky and the
					// timestamp columns scrolled out beside it. Re-enable pinning for
					// this grid so the action icons stay frozen at the far right.
					disableColumnPinning={false}
					// ONLY the actions column is pinned (sticky right). The left
					// columns (checkbox / Status / name) are intentionally NOT pinned:
					// the left pinned block was drawing the boxed "ghost" frame, and
					// pinning around the Status column reordered it under MUI X v9 and
					// blanked its toggles. With no left pin, columns render in natural
					// order (Status toggles paint correctly) and the grid expands full
					// width on the left.
					initialState={{
						...datagridInitialState,
						pinnedColumns: { right: ["actions"] }
					}}
					rowSelectionModel={selectionModel}
					onRowSelectionModelChange={handleRowSelectionChange}
					filterModel={gridFilterModel}
					onFilterModelChange={onFilterModelChange}
					slots={{
						toolbar: CustomToolbar,
						noResultsOverlay: DataGridCustomNoRowsOverlay,
						noRowsOverlay: DataGridCustomNoRowsOverlay
					}}
					slotProps={{
						...( {
							noResultsOverlay: { message: "No rules found." },
							noRowsOverlay: { message: "No rules found." }
						} as Record<string, unknown>),
						loadingOverlay: {
							variant: "skeleton" as const,
							noRowsVariant: "skeleton" as const,
						},
						panel: {
							// MUI X v9: the filter/columns panel anchors to the element
							// passed as `target` (basePopper). This pins it under the
							// funnel button instead of floating mid-grid.
							target: filterButtonEl ?? undefined
						},
						filterPanel: {
							filterFormProps: {
								logicOperatorInputProps: {
									size: "small",
								},
								columnInputProps: {
									size: "small",
								},
								operatorInputProps: {
									size: "small",
								},
								valueInputProps: {
									InputComponentProps: {
										size: "small",
									},
								},
							},
							sx: {
								"& .MuiDataGrid-filterFormLogicOperatorInput": { mr: 2 },
								"& .MuiDataGrid-filterFormColumnInput": { mr: 2, width: 150 },
								"& .MuiDataGrid-filterFormOperatorInput": { mr: 2 },
								"& .MuiDataGrid-filterFormValueInput": { width: 200 }
							},
						},
					}}
					hideFooter
					sortModel={sortModel}
					onSortModelChange={handleSortModelChange}
					columnVisibilityModel={ruleColumnVisibility}
					onColumnVisibilityModelChange={handleRuleColumnVisibilityChange}
				/>
			</CoAppDataGridContainer>
			<RuleDataGridSelectionRow
				selectedRules={selectedRuleIds}
				visibleRuleCount={visibleRuleCount}
				loading={loading}
				shouldShow={selectedRuleIds.length > 0}
				onClearSelectionClickHandler={() => setSelectionModel(emptyGridRowSelectionModel())}
				onDeactivateClickHandler={() => handleBulkStatusUpdateModalToggle("deactivate")}
				onActivateClickHandler={() => handleBulkStatusUpdateModalToggle("activate")}
				onDeleteClickHandler={handleBulkSoftDeleteRuleToggle}
				onTagClickHandler={() => handleBulkApplyModalToggle("tags")}
				onGroupClickHandler={() => handleBulkApplyModalToggle("groups")}
				activateIsDisabled={activateIsDisabled}
				deactivateIsDisabled={deactivateIsDisabled}
			/>
			<CoAppBulkApplyModal
				dialogOpen={bulkApplyModalOpen}
				dialogTitle={`Add/remove ${bulkApplyItemType.toLowerCase().includes("group") ? "groups" : "tags"} for selected rules (${selectedRuleIds.length})`}
				cancelClickHandler={handleBulkApplyModalClose}
				confirmClickhandler={handleBulkApplyConfirmation}
				availableItems={bulkApplyAvailableItems}
				items={bulkApplyItems}
				selectedItemChangeHandler={handleAddOrRemoveItemChange}
				resource={bulkApplyItemType}
				targetResource="rules"
				selectedRules={selectedRuleIds}
				totalRules={rules}
				itemsRemoved={bulkApplyRemovedItems}
				calculateItemsRemovedFromAll={calculateItemsRemovedFromAll}
				calculateItemsToAddToAll={calculateItemsToAddToAll}
				calculateUnchangedItems={calculateUnchangedItems}
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={destructiveModalOpen}
				dialogTitle={`Send ${ruleBeingDeleted.name ?? ""} rule to the trash?`}
				dialogMessage="Are you sure you would like to send this rule within your organization to the trash? This can be restored later."
				confirmClickHandler={handleSoftDeleteRule}
				cancelClickHandler={() => handleDeleteRuleToggle()}
				actionText="Send to trash"
			/>
			<CoAppDestructiveConfirmationModal
				dialogOpen={bulkDestructiveModalOpen}
				dialogTitle={`Send selected (${selectedRuleIds.length}) rules to the trash?`}
				dialogMessage="Are you sure you would like to send the selected rules within your organization to the trash? These can be restored later."
				confirmClickHandler={handleBulkSoftDeleteRulesConfirmation}
				cancelClickHandler={handleBulkSoftDeleteRuleToggle}
				actionText="Send to trash"
			/>
			<CoAppConfirmationModal
				dialogOpen={bulkStatusUpdateOpen}
				dialogTitle={bulkStatusUpdateTitle}
				dialogMessage={`Are you sure you would like to ${isBulkActivating ? "activate" : "deactivate"} the selected ${isBulkActivating ? "inactive" : "active"} rules within your organization?`}
				confirmClickHandler={handleBulkStatusUpdateConfirmation}
				cancelClickHandler={() => setBulkStatusUpdateModalOpen(false)}
				actionText={isBulkActivating ? "Activate" : "Deactivate"}
			/>
			<UndoSnackbar
				level={snackbarLevel}
				message={snackbarMessage}
				open={snackbarOpen}
				setLevel={setSnackbarLevel}
				setMessage={setSnackbarMessage}
				setOpen={setSnackbarOpen}
				undoHandler={isBulkUndo ? handleBulkRuleRestoration : handleRestoreRule}
			/>
		</>
	);
}